import React, { useContext, useEffect, useRef, useState } from 'react'
import { Modal, Platform, Pressable, ScrollView, StyleSheet, Text, TextStyle, View, ViewStyle } from 'react-native'
import { ThemeContext } from '../../context/ThemeContext'
import { textStyles } from '../../../style'
import { Ionicons } from '@expo/vector-icons'
import useScreenSize from '../../screen-size'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import Haptics from "../../haptics"

const LABEL_SPACE = 4
const MENU_PADDING = 10
const MENU_OPTION_ESTIMATED_HEIGHT = 44

type DropdownProps = {
  value: string
  data: any[]
  onChange: (value: any) => void
  displayTextField: string
  valueField: string
  placeholder?: string
  label?: string
  inputStyle?: ViewStyle
  required?: boolean
  errorText?: string
  disabled?: boolean
  onFocus?: () => void
  textStyle?: TextStyle
}

export const ThemedDropdown: React.FC<DropdownProps> = (props: DropdownProps) => {
  const theme = useContext(ThemeContext)
  const {
    primary,
    borderColor,
    textMain,
    placeholder: placeholderColor,
    bgAlt,
  } = theme.theme.colors

  const [isFocused, setIsFocused] = useState(false)
  const [selectDimensions, setSelectDimensions] = useState({ x: 0, y: 0, w: 0, h: 0 })

  const inputRef = useRef<any>(null)
  const { height: screenHeight } = useScreenSize()
  const { bottom: insetBottom } = useSafeAreaInsets()

  useEffect(() => {
    if (props.onFocus) {
      props.onFocus()
    }
  }, [isFocused])

  const selected = props.value ? props.data.find((i) => i[props.valueField] === props.value) : null

  return (
    <View>
      {props.label && (
        <Text
          style={[
            textStyles.label,
            {
              color: isFocused ? primary : textMain,
              marginBottom: LABEL_SPACE,
              marginHorizontal: props.textStyle?.marginHorizontal || 0
            }
          ]}
        >
          {props.label}
          {props.required && <Text style={[textStyles.label, { color: 'red' }]}> *</Text>}
        </Text>
      )}
      {Platform.OS === 'web' ? (
        <select
          value={props.value || props.placeholder}
          disabled={props.disabled}
          onChange={(v) => props.onChange((v.target as any).value)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          style={{
            ...dropdownStyles.dropdownField,
            ...props.inputStyle,
            ...textStyles.body,
            borderColor: isFocused ? primary : borderColor,
            color: props.value ? textMain : placeholderColor,
            backgroundColor: 'transparent',
          }}
        >
          {
            !props.value ? (
              <option value={props.placeholder || 'Select'} disabled selected>
                {props.placeholder}
              </option>
            ) : null
          }
          {props.data.map((item) => (
            <option key={item[props.valueField]} value={item[props.valueField]}>
              {item[props.displayTextField]}
            </option>
          ))}
        </select>
      ) : (
        <>
          <Pressable
            ref={inputRef}
            style={{
              ...dropdownStyles.dropdownField,
              borderColor: isFocused ? primary : borderColor,
              ...props.inputStyle,
            }}
            collapsable={false}
            onPress={() => {
              // Keep anchor/dropdown coordinates in the same space as the Modal window.
              // On Android with edge-to-edge/system bars, prefer pageX/pageY from `measure()`
              // and fallback to `measureInWindow()` if needed.
              requestAnimationFrame(() => {
                const node = inputRef.current
                if (!node) return

                const setDimensionsFromAnchor = (
                  x: number,
                  y: number,
                  width: number,
                  height: number
                ) => {
                  // Always render downward and start at the field top so the menu overlays it.
                  const availableBelow = Math.max(screenHeight - y - insetBottom, height)
                  const estimatedContentHeight = (
                    props.data.length * MENU_OPTION_ESTIMATED_HEIGHT
                  ) + (MENU_PADDING * 2)
                  const menuHeight = Math.min(
                    Math.max(estimatedContentHeight, 0),
                    Math.max(availableBelow, 0)
                  )

                  setSelectDimensions({
                    x,
                    y,
                    w: width,
                    h: menuHeight,
                  })
                }

                if (Platform.OS === 'android' && typeof node.measure === 'function') {
                  node.measure((
                    _x: number,
                    _y: number,
                    width: number,
                    height: number,
                    pageX: number,
                    pageY: number
                  ) => {
                    if (
                      Number.isFinite(pageX) &&
                      Number.isFinite(pageY) &&
                      Number.isFinite(width) &&
                      Number.isFinite(height)
                    ) {
                      setDimensionsFromAnchor(pageX, pageY, width, height)
                      return
                    }

                    node.measureInWindow((
                      x: number,
                      y: number,
                      w: number,
                      h: number
                    ) => setDimensionsFromAnchor(x, y, w, h))
                  })
                  return
                }

                node.measureInWindow((
                  x: number,
                  y: number,
                  width: number,
                  height: number
                ) => setDimensionsFromAnchor(x, y, width, height))
              })
              setIsFocused(true)
            }}
          >
            <Text
              style={[
                textStyles.body,
                {
                  color: props.value ? textMain : placeholderColor,
                  marginRight: 30,
                },
              ]}
            >
              {selected ? selected[props.displayTextField] : props.placeholder || 'Select'}
            </Text>
            <Ionicons name="chevron-down" size={20} color={isFocused ? primary : borderColor} style={{ position: 'absolute', right: 5 }} />
          </Pressable>
          <Modal
            visible={isFocused}
            transparent
            animationType="fade"
            statusBarTranslucent={Platform.OS === 'android'}
            navigationBarTranslucent={Platform.OS === 'android'}
          >
            <Pressable style={{ flex: 1 }} onPress={() => setIsFocused(false)}>
              <View
                style={{
                  position: 'absolute',
                  top: selectDimensions.y,
                  left: selectDimensions.x,
                  backgroundColor: bgAlt,
                  borderRadius: 10,
                  borderWidth: 1,
                  width: selectDimensions.w,
                  maxHeight: selectDimensions.h,
                }}
              >
                <ScrollView contentContainerStyle={{ padding: 10 }}>
                  {props.data.map((item, index) => (
                    <Pressable
                      key={index}
                      onPress={() => {
                        Haptics.selection()
                        props.onChange(item[props.valueField])
                        setIsFocused(false)
                      }}
                    >
                      <Text style={[textStyles.body, { color: textMain, paddingVertical: 10 }]}>
                        {item[props.displayTextField]}
                      </Text>
                    </Pressable>
                  ))}
                </ScrollView>
              </View>
            </Pressable>
          </Modal>
        </>
      )}

      {props.errorText && <Text style={[textStyles.subscript, { color: 'red', marginTop: LABEL_SPACE }]}>{props.errorText}</Text>}
    </View>
  )
}

const dropdownStyles = StyleSheet.create({
  dropdownField: {
    borderWidth: 1,
    paddingLeft: 16,
    borderRadius: 10,
    height: 42,
    justifyContent: 'center',
    outlineWidth: 0,
  },
})


